<template>
  <div class="type-lesson-plan">
    <author-toolbar
      v-if="loggedInUser && loggedInUser.capabilities.includes('publish_posts')"
      :post="resource"
    />

    <local-version-banner v-if="resource.local_version" :content="resource.local_version" />

    <aside
      v-if="resource.parent && resource.parent.lessons"
      class="sidebar-lessons"
      aria-label="List of lessons"
    >
      <div>
        <header>
          <nuxt-link :to="localePath(resource.parent.link)" title="Back to Unit Plan">
            <h4 class="sidebar-subheading">Unit Plan</h4>
            <h3 class="sidebar-heading">
              {{ resource.parent.name }}
            </h3>
          </nuxt-link>
        </header>
        <template v-if="resource.parent.lessons">
          <h5 class="sidebar-subheading">Lessons</h5>
          <lessons-list :lessons="resource.parent.lessons" :active-lesson="resource.id" />
        </template>
      </div>
    </aside>

    <div class="row">
      <nav class="columns small-12 prev-next-nav flex flex-wrap">
        <nuxt-link
          v-if="resource.previous"
          :to="localePath(resource.previous.link)"
          title="View previous lesson"
          class="button button-outlined"
        >
          &larr; {{ resource.lesson_number - 1 }}. {{ resource.previous.name }}
        </nuxt-link>
        <nuxt-link
          v-if="resource.next"
          :to="localePath(resource.next.link)"
          title="View next lesson"
          class="button button-outlined pull-right"
        >
          {{ resource.lesson_number + 1 }}. {{ resource.next.name }} &rarr;
        </nuxt-link>
      </nav>

      <div class="columns small-12 medium-12 large-9">
        <header>
          <div class="text-lesson-plan sidebar-subheading">Lesson Plan</div>
          <h1>{{ resource.name }}</h1>
        </header>

        <template v-if="resource.breadcrumbs">
          <t-breadcrumbs :items="resource.breadcrumbs" class="tw-mt-1" />
        </template>

        <div class="section section-actions flex flex-wrap align-middle">
          <download-button
            :files="resource.files"
            :resource-type="resource.type"
            :is-free="resource.is_free"
          />
          <action-buttons class="pull-right" :resource-id="resource.id" />
        </div>

        <div class="callout callout-details">
          <publisher-link v-if="resource.publisher" :publisher="resource.publisher" />

          <div>
            <template v-if="resource.duration">
              <svg class="icon icon-small tw-inline-block" role="img" aria-hidden="true">
                <use xlink:href="/images/fa-icons.svg#timer"></use>
              </svg>
              {{ resource.duration }} mins
            </template>

            <template v-if="resource.year_levels_range">
              <span>|</span>
              Suitable for {{ $t('grade') }}s:
              <strong>{{ resource.year_levels_range }}</strong>
            </template>
          </div>
        </div>

        <section class="section section-content tw-prose tw-max-w-none">
          <p class="text-big">
            {{ resource.excerpt }}
          </p>
          <div v-if="resource.content" class="description" v-html="resource.content"></div>
          <template v-if="loggedInUser">
            <p v-if="!canAccess" class="callout">Upgrade to view the lesson plan.</p>
          </template>
          <template v-else-if="!resource.is_free">
            <p class="callout">Login to view the lesson plan.</p>
          </template>
        </section>

        <template v-if="canAccess || resource.is_free">
          <div v-if="resource.video_url" class="flex-video">
            <iframe
              width="560"
              height="315"
              :src="'https://www.youtube.com/embed/' + resource.video_url.split('?v=')[1]"
              title="Lesson overview"
              allow="autoplay; encrypted-media"
              allowfullscreen
              webkitallowfullscreen
              msallowfullscreen
            ></iframe>
          </div>

          <section
            v-if="resource.lesson_content_content_description"
            class="section section-content"
          >
            <div class="description" v-html="resource.lesson_content_content_description"></div>
          </section>

          <section v-if="resource.lesson_plan" class="section section-content">
            <div class="description" v-html="resource.lesson_plan"></div>
          </section>

          <section
            v-if="resource.extending_students || resource.supporting_students"
            class="section section-content"
          >
            <h3>Differentiation</h3>
            <template v-if="resource.extending_students">
              <h4>Extending Students</h4>
              <div v-html="resource.extending_students"></div>
            </template>

            <template v-if="resource.supporting_students">
              <h4>Supporting Students</h4>
              <div v-html="resource.supporting_students"></div>
            </template>
          </section>

          <section class="section tw-prose">
            <h3>Suggested Assessment Strategies</h3>
            <ul>
              <li>used strategic whole class or individual questioning</li>
              <li>observed student participation during learning activities</li>
              <li>recorded student progress on a checklist</li>
              <li>annotated student work samples</li>
              <li>collected and reviewed student work samples</li>
              <li>facilitated whole class or peer feedback sessions</li>
              <li>encouraged student self-reflection</li>
              <li>administered formal assessment tasks.</li>
            </ul>
          </section>

          <section v-if="resource.files" class="section section-download">
            <h3>Select a download</h3>
            <div class="box no-padding">
              <download-link
                :files="resource.files"
                :resource-type="resource.type"
                :is-free="resource.is_free"
                :event-params="resource.event_params"
                position="Content | Select a download"
                click-type="Text Link"
                click-text=""
              />
            </div>
          </section>
        </template>

        <section
          v-if="resource.curriculums && resource.curriculums.length > 0"
          class="section section-curriculum"
        >
          <h3>Curriculum</h3>
          <curriculum-list :curriculums="resource.curriculums" />
        </section>

        <section v-if="resource.author" class="section section-author">
          <h3>Author</h3>

          <div class="flex">
            <figure v-if="resource.author && resource.author.photo">
              <img
                :src="resource.author.photo"
                class="circle"
                :alt="'Photo of ' + resource.author.display_name"
                height="42"
                width="42"
              />
            </figure>
            <span>{{ resource.author.display_name }}</span>
          </div>
        </section>

        <section v-if="resource.publisher" class="section section-publisher">
          <div class="flex">
            <figure v-if="resource.publisher && resource.publisher.logo">
              <img
                :src="resource.publisher.logo_square"
                class="circle"
                height="42"
                width="42"
                :alt="resource.publisher.name"
              />
            </figure>
            <div>
              <h3>{{ resource.publisher.name }}</h3>
              <p>{{ resource.publisher.excerpt }}</p>
            </div>
          </div>
        </section>

        <tag-list
          :file-formats="resource.file_formats"
          :tags="resource.tags"
          :year-levels="resource.year_levels"
          :country="resource.country"
        />
      </div>

      <aside class="columns small-12 medium-12 large-3" aria-label="Related resources">
        <div>
          <template v-if="resource.resources && resource.resources.length > 0">
            <h4 class="text-center">
              {{ resource.resources.length }} included
              {{ pluralise('resource', resource.resources.length) }}:
            </h4>

            <ul class="row block-grid block-grid-cards small-up-2 medium-up-2 large-up-1">
              <li v-for="item in resource.resources" :key="item.id">
                <card v-if="item && item.link" type="browse" :item="item" :url="item.link" />
              </li>
            </ul>
          </template>
        </div>
      </aside>
      <country-switcher
        :link="resource.link"
        :linked="resource.linked"
        :country="resource.country"
      />
    </div>
  </div>
</template>

<script>
/* A single lesson plan
 *
 * @parent:
 * @requests: /public/v1/resource
 * @events:
 * @props:
 * @components: ActionButtons, Card, TagList, CommentSection, CurriculumList, DownloadButton, DownloadLink, ImageGallery, ResourcesList
 * @methods:
 */

import { mapGetters } from 'vuex';

import ActionButtons from '~/components/resource/ActionButtons.vue';
import TBreadcrumbs from '~/components/atoms/t-breadcrumbs.vue';
import TagList from '~/components/resource/TagList.vue';
import Card from '~/components/card/Card.vue';
import DownloadLink from '~/components/resource/DownloadLink.vue';
import DownloadButton from '~/components/resource/DownloadButton.vue';
import LessonsList from '~/components/resource/LessonsList.vue';
import CurriculumList from '~/components/resource/CurriculumList.vue';
import PublisherLink from '~/components/PublisherLink.vue';
import CountrySwitcher from '~/components/CountrySwitcher.vue';
import { pluralise } from '~/utils/formatStrings';

export default {
  name: 'LessonPlan',
  components: {
    ActionButtons,
    AuthorToolbar: () => import('~/components/AuthorToolbar.vue'),
    TBreadcrumbs,
    TagList,
    Card,
    DownloadLink,
    DownloadButton,
    LessonsList,
    LocalVersionBanner: () => import('~/components/resource/LocalVersionBanner.vue'),
    CurriculumList,
    PublisherLink,
    CountrySwitcher,
  },
  async asyncData({ app, store, route, params, error }) {
    const data = { types: ['lesson-plan'] };

    try {
      const resource = await store.dispatch('loadResource', {
        id: params.slug,
        types: data.types,
        link: route.path,
        country: app.i18n.locale,
      });

      data.title = resource.name || resource.meta_title;
    } catch (err) {
      // No page matching this route
      error({ statusCode: 404, message: err.message, path: route.path });
    }

    return data;
  },
  data() {
    return {
      title: 'Lesson plan',
      description: 'A lesson plan from Teach Starter',
    };
  },
  jsonld() {
    return this.$jsonSchemaResource(this.resource);
  },
  head() {
    return {
      title: this.title,
      meta: this.$metaPropertiesResource(this.resource),
      link: this.$hrefLangsResource(
        this.resource.linked,
        this.resource.link,
        this.resource.country
      ),
    };
  },
  computed: {
    ...mapGetters(['loggedInUser', 'resource']),
    canAccess() {
      return this.loggedInUser
        ? this.resource.is_free ||
            this.loggedInUser.capabilities.includes('access_product_units_lessons')
        : false;
    },
  },
  mounted() {
    if (this.resource.event_params) {
      this.$mzAnalyticsApi({
        event: 'pageView',
        name: 'lesson_plan',
        productArea: 'lesson_plan',
        entityType: 'lesson_plan',
        entityId: this.resource.id,
      });
    }
  },
  methods: {
    pluralise,
  },
};
</script>

<style scoped lang="scss">
@import '~/assets/scss/sections/_resource-page.scss';
@import '~/assets/scss/sections/_lesson-plan-page.scss';

.prev-next-nav {
  margin-top: $base-padding * 2;
}
</style>
